import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Save, Upload } from "lucide-react";
import { api, formatApiError, uploadImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TABS = ["Hero", "Biography", "Discography", "Awards", "Gallery", "Press", "Event", "Merch", "Media", "Messages"];

function Field({ label, value, onChange, textarea, type, ...rest }) {
  const cls =
    "w-full bg-black/40 border border-white/15 px-4 py-3 text-white font-body text-sm placeholder:text-white/25 focus:outline-none focus:border-gold transition-colors";
  return (
    <label className="block">
      <span className="font-display uppercase tracking-[0.15em] text-[0.62rem] text-white/50 mb-2 block">{label}</span>
      {textarea ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={4} className={`${cls} resize-none`} {...rest} />
      ) : (
        <input type={type || "text"} value={value ?? ""} onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)} className={cls} {...rest} />
      )}
    </label>
  );
}

function ImageField({ label, value, onChange, authHeaders }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadImage(file, authHeaders);
      onChange(url);
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="block">
      <span className="font-display uppercase tracking-[0.15em] text-[0.62rem] text-white/50 mb-2 block">{label}</span>
      <div className="flex gap-3 items-start">
        {value ? (
          <img src={value} alt="preview" className="w-20 h-20 object-cover border border-white/15 shrink-0" />
        ) : (
          <div className="w-20 h-20 border border-dashed border-white/20 shrink-0 flex items-center justify-center text-white/25 text-xs">none</div>
        )}
        <div className="flex-1 space-y-2">
          <input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Paste URL or upload"
            className="w-full bg-black/40 border border-white/15 px-4 py-2.5 text-white font-body text-sm placeholder:text-white/25 focus:outline-none focus:border-gold"
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            data-testid="upload-image-btn"
            className="flex items-center gap-2 border border-white/25 px-4 py-2 font-display uppercase tracking-[0.15em] text-[0.62rem] text-white/70 hover:bg-gold hover:text-black hover:border-gold transition-colors disabled:opacity-50"
          >
            <Upload size={13} /> {busy ? "Uploading..." : "Upload Image"}
          </button>
          <input ref={inputRef} type="file" accept="image/*" onChange={pick} className="hidden" />
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout, authHeaders } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Hero");
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState([]);
  const cfg = { headers: authHeaders };

  useEffect(() => {
    if (user === false) navigate("/admin");
  }, [user, navigate]);

  const load = async () => {
    const { data } = await api.get("/content/all");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (tab === "Messages") api.get("/admin/contact", cfg).then((r) => setMessages(r.data)).catch(() => {});
  }, [tab]);

  if (!data || user === null) {
    return <div className="min-h-screen bg-obsidian flex items-center justify-center text-white/40 font-display tracking-widest">LOADING…</div>;
  }

  const saveSingleton = async (key, payload, endpoint) => {
    try {
      await api.put(endpoint, payload, cfg);
      toast.success(`${key} saved.`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const set = (patch) => setData({ ...data, ...patch });
  const H = data.hero, E = data.event, M = data.media, MR = data.merch;

  return (
    <div className="min-h-screen bg-[#0b0b0b] text-white" data-testid="admin-dashboard">
      <div className="sticky top-0 z-40 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-display font-700 text-lg">TIMBO</span>
            <span className="font-editorial italic text-gold">media portal</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noreferrer" className="font-display uppercase tracking-[0.15em] text-xs text-white/60 hover:text-white">View Site</a>
            <button onClick={() => { logout(); navigate("/admin"); }} data-testid="logout-btn" className="flex items-center gap-2 font-display uppercase tracking-[0.15em] text-xs border border-white/20 px-4 py-2 hover:bg-gold hover:text-black transition-colors">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto px-6 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.toLowerCase()}`}
              className={`font-display uppercase tracking-[0.15em] text-xs px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${tab === t ? "border-gold text-white" : "border-transparent text-white/40 hover:text-white/70"}`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-10">
        {tab === "Hero" && (
          <div className="max-w-2xl space-y-6" data-testid="panel-hero">
            <Field label="Overline" value={H.overline} onChange={(v) => set({ hero: { ...H, overline: v } })} />
            <Field label="Title Line 1" value={H.title_lines?.[0]} onChange={(v) => set({ hero: { ...H, title_lines: [v, ...(H.title_lines?.slice(1) || [])] } })} />
            <Field label="Title Line 2 (optional, shows in gold)" value={H.title_lines?.[1]} onChange={(v) => set({ hero: { ...H, title_lines: v ? [H.title_lines?.[0] || "", v] : [H.title_lines?.[0] || ""] } })} />
            <Field label="Subtitle" textarea value={H.subtitle} onChange={(v) => set({ hero: { ...H, subtitle: v } })} />
            <ImageField label="Background Image" value={H.background_image} authHeaders={authHeaders} onChange={(v) => set({ hero: { ...H, background_image: v } })} />
            <Field label="CTA Label" value={H.cta_label} onChange={(v) => set({ hero: { ...H, cta_label: v } })} />
            <Field label="CTA Target" value={H.cta_target} onChange={(v) => set({ hero: { ...H, cta_target: v } })} />
            <SaveBtn onClick={() => saveSingleton("Hero", H, "/admin/hero")} />
          </div>
        )}

        {tab === "Event" && (
          <div className="max-w-2xl space-y-6" data-testid="panel-event">
            <Field label="Title" value={E.title} onChange={(v) => set({ event: { ...E, title: v } })} />
            <Field label="Tagline" value={E.tagline} onChange={(v) => set({ event: { ...E, tagline: v } })} />
            <Field label="Date" value={E.date} onChange={(v) => set({ event: { ...E, date: v } })} />
            <Field label="Location" value={E.location} onChange={(v) => set({ event: { ...E, location: v } })} />
            <Field label="Description" textarea value={E.description} onChange={(v) => set({ event: { ...E, description: v } })} />
            <Field label="Ticket URL" value={E.ticket_url} onChange={(v) => set({ event: { ...E, ticket_url: v } })} />
            <ImageField label="Image" value={E.image} authHeaders={authHeaders} onChange={(v) => set({ event: { ...E, image: v } })} />
            <div className="space-y-3">
              <span className="font-display uppercase tracking-[0.15em] text-[0.62rem] text-white/50">Stats</span>
              {(E.stats || []).map((s, i) => (
                <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-2 items-center">
                  <input value={s.value} onChange={(e) => { const st = [...E.stats]; st[i] = { ...st[i], value: e.target.value }; set({ event: { ...E, stats: st } }); }} className="bg-black/40 border border-white/15 px-3 py-2 text-sm" />
                  <input value={s.label} onChange={(e) => { const st = [...E.stats]; st[i] = { ...st[i], label: e.target.value }; set({ event: { ...E, stats: st } }); }} className="bg-black/40 border border-white/15 px-3 py-2 text-sm" />
                  <button onClick={() => set({ event: { ...E, stats: E.stats.filter((_, j) => j !== i) } })} className="text-white/40 hover:text-wine p-2"><Trash2 size={16} /></button>
                </div>
              ))}
              <button onClick={() => set({ event: { ...E, stats: [...(E.stats || []), { value: "", label: "" }] } })} className="flex items-center gap-2 text-xs font-display uppercase tracking-widest text-gold"><Plus size={14} /> Add Stat</button>
            </div>
            <SaveBtn onClick={() => saveSingleton("Event", E, "/admin/event")} />
          </div>
        )}

        {tab === "Merch" && (
          <div className="max-w-2xl space-y-6" data-testid="panel-merch">
            <Field label="Overline" value={MR.overline} onChange={(v) => set({ merch: { ...MR, overline: v } })} />
            <Field label="Heading" value={MR.heading} onChange={(v) => set({ merch: { ...MR, heading: v } })} />
            <Field label="Description" textarea value={MR.description} onChange={(v) => set({ merch: { ...MR, description: v } })} />
            <ImageField label="Background Image" value={MR.image} authHeaders={authHeaders} onChange={(v) => set({ merch: { ...MR, image: v } })} />
            <Field label="Merch URL" value={MR.merch_url} onChange={(v) => set({ merch: { ...MR, merch_url: v } })} />
            <Field label="Merch Button Label" value={MR.merch_label} onChange={(v) => set({ merch: { ...MR, merch_label: v } })} />
            <Field label="MasterClass URL" value={MR.masterclass_url} onChange={(v) => set({ merch: { ...MR, masterclass_url: v } })} />
            <Field label="MasterClass Button Label" value={MR.masterclass_label} onChange={(v) => set({ merch: { ...MR, masterclass_label: v } })} />
            <SaveBtn onClick={() => saveSingleton("Merch", MR, "/admin/merch")} />
          </div>
        )}

        {tab === "Media" && (
          <div className="max-w-2xl space-y-6" data-testid="panel-media">
            <Field label="Spotify Embed URL" value={M.spotify_embed} onChange={(v) => set({ media: { ...M, spotify_embed: v } })} />
            <Field label="YouTube Embed URL" value={M.youtube_embed} onChange={(v) => set({ media: { ...M, youtube_embed: v } })} />
            <SaveBtn onClick={() => saveSingleton("Media", M, "/admin/media")} />
          </div>
        )}

        {tab === "Biography" && (
          <ListEditor testid="panel-biography" items={data.bio} reload={load} cfg={cfg} authHeaders={authHeaders} endpoint="/admin/bio"
            emptyItem={{ num: "", title: "", subtitle: "", body: "", image: "", order: (data.bio?.length || 0) }}
            fields={[
              { key: "num", label: "Number (e.g. 06)" },
              { key: "subtitle", label: "Subtitle" },
              { key: "title", label: "Title" },
              { key: "body", label: "Body", textarea: true },
              { key: "image", label: "Image", image: true },
              { key: "order", label: "Order", type: "number" },
            ]}
            titleOf={(it) => `${it.num} · ${it.title}`} />
        )}

        {tab === "Discography" && (
          <ListEditor testid="panel-discography" items={data.discography} reload={load} cfg={cfg} authHeaders={authHeaders} endpoint="/admin/discography"
            emptyItem={{ title: "", artist: "", year: "", role: "", cover_image: "", accent: "#4FB04A", preview_url: "", spotify_url: "", order: (data.discography?.length || 0) }}
            fields={[
              { key: "title", label: "Track / Album Title" },
              { key: "artist", label: "Artist" },
              { key: "year", label: "Year" },
              { key: "role", label: "Role" },
              { key: "cover_image", label: "Cover Art", image: true },
              { key: "preview_url", label: "Audio Preview URL (mp3/m4a)" },
              { key: "spotify_url", label: "Spotify URL" },
              { key: "accent", label: "Accent Color (hex)" },
              { key: "order", label: "Order", type: "number" },
            ]}
            titleOf={(it) => `${it.title}, ${it.artist}`} />
        )}

        {tab === "Awards" && (
          <ListEditor testid="panel-awards" items={data.awards} reload={load} cfg={cfg} authHeaders={authHeaders} endpoint="/admin/awards"
            emptyItem={{ year: "", title: "", category: "", work: "", result: "Won", order: (data.awards?.length || 0) }}
            fields={[
              { key: "year", label: "Year" },
              { key: "title", label: "Award Title" },
              { key: "category", label: "Category" },
              { key: "work", label: "Work / Detail" },
              { key: "result", label: "Result (Won / Nominated)" },
              { key: "order", label: "Order", type: "number" },
            ]}
            titleOf={(it) => `${it.year} · ${it.title}, ${it.result}`} />
        )}

        {tab === "Gallery" && (
          <ListEditor testid="panel-gallery" items={data.gallery} reload={load} cfg={cfg} authHeaders={authHeaders} endpoint="/admin/gallery"
            emptyItem={{ image: "", caption: "", span: "col-span-12 md:col-span-4", order: (data.gallery?.length || 0) }}
            fields={[
              { key: "image", label: "Image", image: true },
              { key: "caption", label: "Caption" },
              { key: "span", label: "Grid Span (e.g. col-span-12 md:col-span-6)" },
              { key: "order", label: "Order", type: "number" },
            ]}
            titleOf={(it) => it.caption || it.image} />
        )}

        {tab === "Press" && (
          <ListEditor testid="panel-press" items={data.press} reload={load} cfg={cfg} authHeaders={authHeaders} endpoint="/admin/press"
            emptyItem={{ outlet: "", title: "", image: "", url: "", order: (data.press?.length || 0) }}
            fields={[
              { key: "outlet", label: "Outlet" },
              { key: "title", label: "Headline" },
              { key: "image", label: "Image", image: true },
              { key: "url", label: "Article URL" },
              { key: "order", label: "Order", type: "number" },
            ]}
            titleOf={(it) => `${it.outlet}, ${it.title}`} />
        )}

        {tab === "Messages" && (
          <div className="space-y-4" data-testid="panel-messages">
            {messages.length === 0 && <p className="text-white/40 font-body">No messages.</p>}
            {messages.map((m) => (
              <div key={m.id} className="border border-white/10 bg-white/[0.02] p-6" data-testid="message-item">
                <div className="flex justify-between mb-2">
                  <span className="font-display uppercase tracking-[0.15em] text-sm text-gold">{m.name}</span>
                  <span className="text-xs text-white/40">{m.email}</span>
                </div>
                <p className="text-white/70 font-body">{m.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SaveBtn({ onClick }) {
  return (
    <button onClick={onClick} data-testid="save-btn" className="flex items-center gap-2 bg-gold text-black font-display uppercase tracking-[0.2em] text-xs px-8 py-3 hover:bg-white transition-colors">
      <Save size={15} /> Save Changes
    </button>
  );
}

function ListEditor({ items = [], fields, emptyItem, endpoint, cfg, reload, titleOf, testid, authHeaders }) {
  const [editing, setEditing] = useState(null);

  const save = async (item) => {
    try {
      if (item.id) await api.put(`${endpoint}/${item.id}`, item, cfg);
      else await api.post(endpoint, item, cfg);
      toast.success("Saved.");
      setEditing(null);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`${endpoint}/${id}`, cfg); toast.success("Deleted."); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid={testid}>
      <button onClick={() => setEditing({ ...emptyItem })} data-testid="add-item-btn" className="flex items-center gap-2 mb-6 bg-gold text-black font-display uppercase tracking-[0.2em] text-xs px-6 py-3 hover:bg-white transition-colors">
        <Plus size={15} /> Add New
      </button>

      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between border border-white/10 bg-white/[0.02] px-5 py-4">
            <span className="font-body text-sm text-white/80 truncate">{titleOf(it)}</span>
            <div className="flex gap-3 shrink-0">
              <button onClick={() => setEditing({ ...it })} data-testid="edit-item-btn" className="font-display uppercase text-xs tracking-widest text-white/60 hover:text-gold">Edit</button>
              <button onClick={() => remove(it.id)} data-testid="delete-item-btn" className="text-white/40 hover:text-wine"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-6" onClick={() => setEditing(null)}>
          <div className="bg-[#111] border border-white/10 max-w-xl w-full max-h-[85vh] overflow-y-auto p-8 space-y-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-editorial italic text-2xl text-white mb-4">{editing.id ? "Edit Item" : "New Item"}</h3>
            {fields.map((f) => (
              f.image ? (
                <ImageField key={f.key} label={f.label} authHeaders={authHeaders} value={editing[f.key]} onChange={(v) => setEditing({ ...editing, [f.key]: v })} />
              ) : (
                <Field key={f.key} label={f.label} textarea={f.textarea} type={f.type} value={editing[f.key]} onChange={(v) => setEditing({ ...editing, [f.key]: v })} />
              )
            ))}
            <div className="flex gap-3 pt-2">
              <button onClick={() => save(editing)} data-testid="modal-save-btn" className="bg-gold text-black font-display uppercase tracking-[0.2em] text-xs px-8 py-3 hover:bg-white transition-colors">Save</button>
              <button onClick={() => setEditing(null)} className="border border-white/20 font-display uppercase tracking-[0.2em] text-xs px-8 py-3 hover:bg-white/10 transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
